import { all, takeEvery, takeLatest, put, call, select } from 'redux-saga/effects';
import renderNotification from '../../helpers/notifications/renderNotification';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import { selectSelectedOrganizationId } from '../organizations/selectors';
import { selectCurrentUser } from '../authentication/selectors';
import { onCreateUser, onGetUserRoles, onGetUsers, onUpdateUser, onDeleteUser } from './api';

import {
  getUsers,
  getUsersSucceeded,
  getUsersFailed,
  getUserRoles,
  getUserRolesSucceeded,
  getUserRolesFailed,
  createUser,
  createUserSucceeded,
  createUserFailed,
  updateUser,
  updateUserSucceeded,
  updateUserFailed,
  deleteUser,
  deleteUserSucceeded,
  deleteUserFailed,
  bulkEditUsers,
  bulkEditUsersSucceeded,
  bulkEditUsersFailed
} from './slice';

const { user_create_success, user_update_success, user_delete_success } = NOTIFICATION_MESSAGES;

export function* getUsersWorker({ payload }) {
  const { organizationId } = payload;

  try {
    const response = yield call(onGetUsers, { organization_Id: organizationId });
    yield put(getUsersSucceeded(response.data));
  } catch {
    yield put(getUsersFailed());
  }
}

export function* getUserRolesWorker({ payload }) {
  const { organisationType } = payload;

  try {
    const response = yield call(onGetUserRoles, organisationType);
    yield put(getUserRolesSucceeded(response.data));
  } catch {
    yield put(getUserRolesFailed());
  }
}

export function* createUserWorker({ payload }) {
  const { user, callback, renderSuccess = true } = payload;

  try {
    yield call(onCreateUser, user);
    if (renderSuccess) {
      renderNotification({ type: 'success', message: user_create_success });
    }
    yield put(createUserSucceeded());
    callback?.();
  } catch {
    yield put(createUserFailed());
  }
}

export function* updateUserWorker({ payload = {} }) {
  const { userId, user, callback, renderSuccess = true } = payload;

  try {
    yield call(onUpdateUser, userId, user);
    if (renderSuccess) {
      renderNotification({ type: 'success', message: user_update_success });
    }
    yield put(updateUserSucceeded());
    callback?.();
  } catch {
    yield put(updateUserFailed());
  }
}

export function* deleteUserWorker({ payload }) {
  const { userId, callback, renderSuccess = true } = payload;

  try {
    yield call(onDeleteUser, userId);
    if (renderSuccess) {
      renderNotification({ type: 'success', message: user_delete_success });
    }
    yield put(deleteUserSucceeded());
    callback?.();
  } catch {
    yield put(deleteUserFailed());
  }
}

export function* getUsersByOrganizationWorker() {
  const selectedOrganizationId = yield select(selectSelectedOrganizationId);
  if (selectedOrganizationId) {
    yield put(getUsers({ organizationId: selectedOrganizationId }));
  }
}

export function* bulkEditUsersWorker({ payload }) {
  const { usersToProcess, deletedUsers, callback } = payload;

  const usersToCreate = usersToProcess.filter((user) => user.isNew);
  const usersToUpdate = usersToProcess.filter((user) => user.isModified);

  try {
    for (const user of deletedUsers) {
      yield call(deleteUserWorker, {
        payload: { userId: user.id, renderSuccess: false }
      });
    }

    for (const user of usersToCreate) {
      const { first_name, last_name, email, role, organization_id } = user;
      const apiUser = { first_name, last_name, email, role, organization_id };

      yield call(createUserWorker, {
        payload: { user: apiUser, renderSuccess: false }
      });
    }

    for (const user of usersToUpdate) {
      const { first_name, last_name, email, role, organization_id } = user;
      const apiUser = { first_name, last_name, email, role, organization_id };

      yield call(updateUserWorker, {
        payload: { userId: user.id, user: apiUser, renderSuccess: false }
      });
    }

    const currentUser = yield select(selectCurrentUser);

    yield put(getUsers({ organizationId: currentUser?.organization_id }));
    yield put(bulkEditUsersSucceeded());

    if (callback) {
      yield call(callback);
    }
  } catch {
    yield put(bulkEditUsersFailed());
  }
}

export default function* usersSaga() {
  yield all([
    takeLatest(getUsers.type, getUsersWorker),
    takeLatest(getUserRoles.type, getUserRolesWorker),

    takeLatest(createUser.type, createUserWorker),
    takeLatest(updateUser.type, updateUserWorker),
    takeLatest(deleteUser.type, deleteUserWorker),

    takeEvery(
      [createUserSucceeded.type, updateUserSucceeded.type, deleteUserSucceeded.type],
      getUsersByOrganizationWorker
    ),
    takeEvery(bulkEditUsers.type, bulkEditUsersWorker)
  ]);
}
